export const apiRoute = 'https://cms.les-eterpaz.ch';

export type ApiMemberType = {
  id: number;
  name: string;
  photo: string;
  translations: { languages_code: 'fr-FR'; introText: string; description?: string }[];
};

export type MemberType = {
  id: string;
  name: string;
  photoUrl: string;
  introText: string;
  description?: string;
};

/**
 * fetch() + JSON parsing that actually rejects on a non-2xx response, instead of
 * silently trying to parse an error page/body as JSON.
 */
// eslint-disable-next-line no-undef -- RequestInit is a DOM lib type, not a JS global
export async function fetchJson<T = unknown>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`Request to ${url} failed: HTTP ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function fetchMembers(): Promise<MemberType[]> {
  return fetchJson(
    `${apiRoute}/items/TeamMembers?filter[status][_eq]=published&sort=sort&fields=*,translations.*`
  );
}

/**
 * Convertit un texte Markdown simple en HTML et nettoie les échappements \"
 * Gère : **gras**, *italique*, et [texte](url) pour les liens, ainsi que les retours à la ligne.
 * @param markdownText Texte Markdown
 * @returns HTML prêt à passer dans dangerouslySetInnerHTML
 */
export function parseSimpleMarkdown(markdownText?: string) {
  if (!markdownText) return '';

  // 1. Nettoyer les échappements \"
  let text = markdownText.replace(/\\\\"/g, '"');

  // 2. Échapper les caractères HTML sensibles pour éviter l'injection
  text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // 3. Convertir les liens : [texte](url) -> <a href="url" target="_blank" rel="noopener noreferrer">texte</a>
  // Seuls les schémas http(s)/mailto sont autorisés, et les guillemets sont échappés,
  // pour empêcher une URL malveillante (ex. javascript:, ou contenant un ") d'injecter
  // du HTML/JS dans l'attribut href.
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label: string, url: string) => {
    const safeUrl = /^(https?:|mailto:)/i.test(url.trim())
      ? url.trim().replace(/"/g, '&quot;')
      : null;
    return safeUrl
      ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`
      : label;
  });

  // 4. Gras : **texte**
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // 5. Italique : *texte*
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // 6. Retours à la ligne : \n -> <br>
  text = text.replace(/\n/g, '<br>');

  return text;
}

export type DirectusFile = {
  id: string;
  title: string;
  description?: string;
  type?: string;
  tags?: string[];
  slug: string;
};

export type DirectusFileFk = {
  id: number;
  sort?: number;
  directus_files_id: DirectusFile;
};

// Objet reçu de l'API
export type CghApiType = {
  translations: ConditionsHebergementType[];
};

// Objet de travail
export interface ConditionsHebergementType {
  id: number;
  languages_code: { code: 'fr-FR' | 'de-DE' | 'en-GB' };
  A_title: string;
  A_description?: string;
  A_Conditions: DirectusFileFk[];
  B_title: string;
  B_description?: string;
  B1_NuitesMinimumParSejour: DirectusFile;
  C_title: string;
  C_description?: string;
  C1_ConditionDAnnulation: DirectusFile;
  D_title: string;
  D_description?: string;
  D_Nettoyages: DirectusFileFk[];
}

// Objet reçu de l'API
export type CgaApiType = {
  translations: ConditionsAnimationsType[];
};

// Objet de travail
export interface ConditionsAnimationsType {
  id: number;
  languages_code: { code: 'fr-FR' | 'de-DE' | 'en-GB' };
  E_title: string;
  E_description?: string;
  E_Packs: DirectusFileFk[];
  F_title: string;
  F_description?: string;
  F_Animations: DirectusFileFk[];
  G_title: string;
  G_description?: string;
  G_Location: DirectusFileFk[];
}

export function GetFilesPerTag(tag: string, files?: DirectusFile[]): DirectusFile[] {
  if (files && files.length > 0 && tag) {
    return files.filter((f) => f.tags?.includes(tag));
  }
  return [];
}

export const getUrlFromSlug = (slug: string, files?: DirectusFile[]) =>
  files?.find((file) => file.slug === slug);

// CAMP FLOORS

export type PlansColonieType = {
  campFloors: DirectusFile;
  campExterior: DirectusFile;
};

export type ApiFloorType = {
  id: number;
  slug: string;
  image: string;
  translations: {
    id: number;
    languages_code: string;
    name: string;
    description: string;
  }[];
};

export type ApiExtLinkType = {
  id: number;
  languages_code: string;
  electrobroc: string;
  mdg: string;
  mc: string;
  gruyeres: string;
};
